import React, { useMemo, useState } from 'react';
import { useApi } from '@axiom/ui';
import type { User } from '@axiom/validation';

import { LegacyUserApi } from '../../api/legacy-user-api';
import { EnvUtil } from '../../utils/env-util';

import { AdminModalSearchListDetail } from './AdminModalSearchListDetail';
import { AdminModalSearchList } from './AdminModalSearchList';
import { AdminModalDetail } from './AdminModalDetail';
import { AdminModalUsersForm } from './AdminModalUsersForm';

const fullName = (user: User) =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

export const AdminModalUsersView = () => {
  const [{ data: users }] = useApi(LegacyUserApi.readUsers());
  const [selected, setSelected] = useState<User | null>(null);
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.toLowerCase();
    const filtered = term
      ? users.filter(user => fullName(user).toLowerCase().includes(term))
      : users;

    return [...filtered].sort((a, b) => {
      if (!!a.isActive !== !!b.isActive) {
        return a.isActive ? -1 : 1;
      }
      return fullName(a).localeCompare(fullName(b));
    });
  }, [users, search]);

  return (
    <AdminModalSearchListDetail
      name="MANAGE_USERS"
      list={
        <AdminModalSearchList<User>
          name="USER_LIST"
          searchName="USER_SEARCH_INPUT"
          searchPlaceholder="Search users"
          onSearchChange={setSearch}
          items={visible}
          selectedId={selected?.id ?? null}
          onSelect={setSelected}
          renderRow={user => ({
            content: fullName(user),
            inactive: !user.isActive,
          })}
          addControl={{
            kind: 'link',
            label: 'To create a new user, contact us at Service Cloud',
            name: 'USER_HELP_LINK',
            href: EnvUtil.reportAnIssueUrl,
          }}
        />
      }
      detail={
        <AdminModalDetail
          emptyText="Select a search result."
          emptyName="NO_USER_SELECTED"
        >
          {selected ? (
            <AdminModalUsersForm user={selected} key={selected.id} />
          ) : null}
        </AdminModalDetail>
      }
    />
  );
};
